import React, { useState, useEffect } from 'react';
import {
  DollarSign, Lock, Unlock, AlertTriangle,
  CreditCard, Banknote, Building, X, Store,
  TrendingUp, RefreshCw, Filter, Printer,
  Clock, Gem, Users, ChevronDown, ChevronUp,
  ArrowLeft, Eye, ShoppingBag
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api, { API_URL } from '../../services/api';

const CaisseGenerale = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtres
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const firstOfMonth = today.slice(0, 8) + '01';
  const [dateDebut, setDateDebut] = useState(firstOfMonth);
  const [dateFin, setDateFin] = useState(today);
  const [showFilters, setShowFilters] = useState(false);

  // Boutique selectionnee (detail)
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  const [boutiqueRapport, setBoutiqueRapport] = useState(null);
  const [loadingBoutique, setLoadingBoutique] = useState(false);

  // Comptes clients
  const [comptesClients, setComptesClients] = useState([]);
  const [showComptes, setShowComptes] = useState(false);
  const [filtreCompte, setFiltreCompte] = useState('');
  const [filtreType, setFiltreType] = useState('tous');

  // Detail cloture
  const [showDetailModal, setShowDetailModal] = useState(null);

  // Impression
  const [selectedBoutiquesImpr, setSelectedBoutiquesImpr] = useState([]);
  const [showImprModal, setShowImprModal] = useState(false);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' MAD';
  // Version PDF-safe (remplace les espaces insecables par des espaces normaux)
  const fmtPdf = (m) => { const n = parseFloat(m||0); const parts = n.toFixed(2).split('.'); parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '); return parts.join(',') + ' MAD'; };
  const fmtDate = (d) => { if (!d) return '-'; return new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); };
  const fmtHeure = (d) => { if (!d) return '-'; return new Date(d).toLocaleString('fr-FR', { hour:'2-digit', minute:'2-digit' }); };

  const ecartColor = (e) => {
    const a = Math.abs(e||0);
    if (a === 0) return 'text-green-600 bg-green-50 border-green-200';
    if (a < 100) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const modes = [
    { key: 'especes', label: 'ESPECES', icon: Banknote, color: 'from-green-500 to-emerald-600', text: 'text-green-700' },
    { key: 'carte', label: 'CARTE', icon: CreditCard, color: 'from-blue-500 to-blue-600', text: 'text-blue-700' },
    { key: 'cheque', label: 'CHEQUE', icon: Building, color: 'from-indigo-500 to-indigo-600', text: 'text-indigo-700' },
    { key: 'virement', label: 'VIREMENT', icon: DollarSign, color: 'from-purple-500 to-purple-600', text: 'text-purple-700' },
    { key: 'or', label: 'OR', icon: Gem, color: 'from-yellow-500 to-amber-600', text: 'text-yellow-700' },
    { key: 'compte', label: 'COMPTE', icon: Users, color: 'from-gray-500 to-gray-600', text: 'text-gray-700' },
  ];

  // --- API ---
  const loadData = async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      const r = await api.get(`${API_URL}/clotures-caisse/generale?${params.toString()}`);
      setData(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setLoading(false); }
  };

  const loadBoutiqueDetail = async (boutiqueId, boutiqueNom) => {
    setLoadingBoutique(true);
    setSelectedBoutique({ id: boutiqueId, nom: boutiqueNom });
    try {
      const r = await api.get(`${API_URL}/clotures-caisse/rapport-journalier?boutique_id=${boutiqueId}`);
      setBoutiqueRapport(r.data);
    } catch (err) { console.error(err); }
    finally { setLoadingBoutique(false); }
  };

  const loadComptes = async () => {
    try {
      const params = new URLSearchParams();
      if (filtreCompte) params.append('search', filtreCompte);
      if (filtreType !== 'tous') params.append('filtre', filtreType);
      const r = await api.get(`${API_URL}/clotures-caisse/comptes-clients?${params.toString()}`);
      setComptesClients(r.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadData(); loadComptes(); /* eslint-disable-next-line */ }, []);

  // --- Impression PDF ---
  // --- PDF helpers ---
  const pdfHeader = (doc, titre, sousTitre) => {
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(67, 56, 202);
    doc.rect(0, 0, pageW, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(titre, pageW / 2, 11, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(sousTitre, pageW / 2, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    return 36;
  };

  const pdfEncaissements = (doc, y, enc) => {
    autoTable(doc, {
      startY: y,
      head: [['Mode de paiement', 'Montant']],
      body: [
        ['Especes', fmtPdf(enc?.especes)],
        ['Carte bancaire', fmtPdf(enc?.carte)],
        ['Cheque', fmtPdf(enc?.cheque)],
        ['Virement', fmtPdf(enc?.virement)],
        ['Or', fmtPdf(enc?.or)],
        ['Compte client', fmtPdf(enc?.compte)],
      ],
      foot: [['TOTAL', fmtPdf(enc?.total)]],
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      footStyles: { fillColor: [67, 56, 202], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    return doc.lastAutoTable.finalY + 6;
  };

  const pdfVentes = (doc, y, ventes) => {
    if (!ventes || ventes.length === 0) return y;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Ventes (${ventes.length})`, 14, y);
    autoTable(doc, {
      startY: y + 2,
      head: [['Heure', 'Article', 'Client', 'Mode', 'Prix', 'Encaisse']],
      body: ventes.map(v => [
        fmtHeure(v.date_vente),
        v.bijou_nom || v.reference_article || '-',
        v.client_nom || '-',
        v.mode_paiement || '-',
        fmtPdf(v.prix_vente),
        fmtPdf(v.montant_final)
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      columnStyles: { 4:{halign:'right'}, 5:{halign:'right'} },
      margin: { left: 14, right: 14 },
    });
    return doc.lastAutoTable.finalY + 6;
  };

  // --- Impression boutique individuelle ---
  const imprimerBoutique = () => {
    if (!boutiqueRapport || !selectedBoutique) return;
    const doc = new jsPDF();
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
    const heureStr = now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
    const enc = boutiqueRapport.encaissements || {};
    const res = boutiqueRapport.resume || {};

    let y = pdfHeader(doc, `CAISSE - ${selectedBoutique.nom}`, `${dateStr} - ${heureStr}`);
    doc.setFontSize(10);
    doc.text(`Ventes: ${res.nb_ventes || 0}`, 14, y);
    doc.text(`Total: ${fmtPdf(res.total_encaisse)}`, doc.internal.pageSize.getWidth() - 14, y, { align: 'right' });
    y += 7;
    y = pdfEncaissements(doc, y, enc);
    y = pdfVentes(doc, y, boutiqueRapport.ventes);

    doc.save(`caisse_${selectedBoutique.nom.replace(/\s/g, '_')}_${today}.pdf`);
  };

  // --- Impression toutes boutiques (separe par boutique) ---
  const imprimerTout = async () => {
    if (!data) return;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
    const heureStr = now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });

    let y = pdfHeader(doc, 'CAISSE GENERALE - TOUTES BOUTIQUES', `${dateStr} - ${heureStr}`);

    // Charger le rapport journalier de chaque boutique qui a des ventes
    const boutiquesAvecVentes = (data.par_boutique || []).filter(b => b.nb_ventes > 0);

    if (boutiquesAvecVentes.length === 0) {
      doc.setFontSize(12);
      doc.text('Aucune vente sur la periode', pageW / 2, y + 20, { align: 'center' });
      doc.save(`caisse_generale_${today}.pdf`);
      return;
    }

    // Totaux consolides d'abord
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('RESUME CONSOLIDE', 14, y);
    y += 2;
    autoTable(doc, {
      startY: y,
      head: [['Boutique', 'Ventes', 'Especes', 'Carte', 'Cheque', 'Virement', 'Total']],
      body: boutiquesAvecVentes.map(b => [
        b.boutique_nom, b.nb_ventes || 0,
        fmtPdf(b.especes), fmtPdf(b.carte), fmtPdf(b.cheque), fmtPdf(b.virement), fmtPdf(b.total_theorique)
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [67, 56, 202], textColor: 255 },
      columnStyles: { 2:{halign:'right'}, 3:{halign:'right'}, 4:{halign:'right'}, 5:{halign:'right'}, 6:{halign:'right'} },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 4;

    // Grand total
    doc.setFillColor(67, 56, 202);
    doc.setTextColor(255);
    doc.rect(14, y, pageW - 28, 10, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL GENERAL', 18, y + 7);
    const grandTotal = boutiquesAvecVentes.reduce((s, b) => s + parseFloat(b.total_theorique || 0), 0);
    doc.text(fmtPdf(grandTotal), pageW - 18, y + 7, { align: 'right' });
    doc.setTextColor(0);
    y += 16;

    // Detail par boutique - charger les rapports individuels
    for (const b of boutiquesAvecVentes) {
      try {
        const r = await api.get(`${API_URL}/clotures-caisse/rapport-journalier?boutique_id=${b.boutique_id}`);
        const bData = r.data;

        // Nouvelle page si pas assez de place
        if (y > 200) { doc.addPage(); y = 15; }

        // Titre boutique
        doc.setFillColor(34, 197, 94);
        doc.rect(14, y, pageW - 28, 8, 'F');
        doc.setTextColor(255);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(b.boutique_nom, 18, y + 5.5);
        doc.text(`${bData.resume?.nb_ventes || 0} ventes - ${fmtPdf(bData.resume?.total_encaisse)}`, pageW - 18, y + 5.5, { align: 'right' });
        doc.setTextColor(0);
        y += 12;

        y = pdfEncaissements(doc, y, bData.encaissements);
        y = pdfVentes(doc, y, bData.ventes);
        y += 4;
      } catch (err) {
        console.error(`Erreur chargement boutique ${b.boutique_nom}:`, err);
      }
    }

    doc.save(`caisse_generale_${today}.pdf`);
  };

  // --- Render ---
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  const { boutiques = [], consolide = {}, par_boutique = [], historique = [] } = data || {};
  const nbOuvertes = boutiques.filter(b => b.statut === 'ouverte').length;

  // --- VUE DETAIL BOUTIQUE ---
  if (selectedBoutique) {
    const enc = boutiqueRapport?.encaissements || {};
    const res = boutiqueRapport?.resume || {};
    const ventesJour = boutiqueRapport?.ventes || [];

    return (
      <div className="min-h-screen bg-gray-100 pb-8">
        {/* Header boutique */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button onClick={() => { setSelectedBoutique(null); setBoutiqueRapport(null); }}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold">{selectedBoutique.nom}</h1>
                  <p className="text-green-200 text-sm">Caisse du {new Date().toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</p>
                </div>
              </div>
              <button onClick={imprimerBoutique} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2.5 rounded-lg">
                <Printer className="h-4 w-4" /><span>Imprimer</span>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
          {loadingBoutique ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border p-4">
                  <p className="text-xs text-gray-500">Ventes</p>
                  <p className="text-2xl font-black text-gray-900">{res.nb_ventes || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-4">
                  <p className="text-xs text-gray-500">Total encaisse</p>
                  <p className="text-xl font-black text-green-700">{fmt(res.total_encaisse)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-4">
                  <p className="text-xs text-gray-500">Prix vente</p>
                  <p className="text-xl font-black text-gray-700">{fmt(res.total_prix_vente)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border p-4">
                  <p className="text-xs text-gray-500">Remises</p>
                  <p className="text-xl font-black text-orange-600">{fmt(res.total_remises)}</p>
                </div>
              </div>

              {/* Encaissements */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {modes.map(m => {
                  const montant = enc[m.key] || 0;
                  const Icon = m.icon;
                  return (
                    <div key={m.key} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                      <div className={`bg-gradient-to-r ${m.color} px-4 py-2 flex items-center space-x-2`}>
                        <Icon className="h-4 w-4 text-white" />
                        <span className="text-white text-xs font-bold">{m.label}</span>
                      </div>
                      <div className="px-4 py-3">
                        <p className={`text-2xl font-black ${m.text}`}>{fmt(montant)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-xl p-5 flex items-center justify-between shadow-lg">
                <p className="text-white font-medium">TOTAL ENCAISSE</p>
                <p className="text-3xl font-black text-white">{fmt(enc.total)}</p>
              </div>

              {/* Ventes */}
              {ventesJour.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border">
                  <div className="px-5 py-4 border-b">
                    <h2 className="font-bold text-gray-800 flex items-center space-x-2">
                      <ShoppingBag className="h-5 w-5 text-green-600" /><span>VENTES DU JOUR ({ventesJour.length})</span>
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Heure</th>
                          <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Article</th>
                          <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Client</th>
                          <th className="px-4 py-2 text-center text-xs text-gray-500 uppercase">Mode</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase">Prix</th>
                          <th className="px-4 py-2 text-right text-xs text-gray-500 uppercase">Encaisse</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {ventesJour.map(v => (
                          <tr key={v.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-600">{fmtHeure(v.date_vente)}</td>
                            <td className="px-4 py-2 font-medium">{v.bijou_nom || v.reference_article || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{v.client_nom || '-'}</td>
                            <td className="px-4 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded bg-gray-100">{v.mode_paiement}</span></td>
                            <td className="px-4 py-2 text-right">{fmt(v.prix_vente)}</td>
                            <td className="px-4 py-2 text-right font-bold">{fmt(v.montant_final)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // --- VUE PRINCIPALE : TOUTES BOUTIQUES ---
  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Caisse Generale</h1>
              <p className="text-indigo-200 text-sm">Vue consolidee - toutes boutiques</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                <Filter className="h-4 w-4" /><span>Periode</span>
              </button>
              <button onClick={imprimerTout} className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg">
                <Printer className="h-4 w-4" /><span>Imprimer tout</span>
              </button>
              <button onClick={() => { loadData(); loadComptes(); }} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
          {showFilters && (
            <div className="mt-4 bg-white/10 rounded-xl p-4 flex flex-col sm:flex-row items-end gap-3">
              <div className="w-full sm:w-auto">
                <label className="block text-xs text-indigo-200 mb-1">Du</label>
                <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" />
              </div>
              <div className="w-full sm:w-auto">
                <label className="block text-xs text-indigo-200 mb-1">Au</label>
                <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" />
              </div>
              <button onClick={loadData} className="w-full sm:w-auto px-6 py-2 bg-white text-indigo-700 font-medium rounded-lg hover:bg-indigo-50">Appliquer</button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><div className="flex items-center space-x-2"><AlertTriangle className="h-5 w-5" /><span>{error}</span></div><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}

        {/* ===== BOUTIQUES - CARTES CLIQUABLES ===== */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center space-x-2">
            <Store className="h-5 w-5 text-indigo-600" /><span>BOUTIQUES</span>
            <span className="ml-2 text-sm font-normal text-gray-500">Cliquez pour voir le detail</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {boutiques.map(b => (
              <div key={b.id}
                onClick={() => loadBoutiqueDetail(b.id, b.nom)}
                className="bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${b.statut === 'ouverte' ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                    <p className="font-bold text-gray-900">{b.nom}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${b.statut === 'ouverte' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {b.statut === 'ouverte' ? 'Ouverte' : 'Fermee'}
                  </span>
                </div>
                {/* Totaux de la boutique si on a les donnees */}
                {(() => {
                  const pb = par_boutique.find(p => p.boutique_id === b.id);
                  if (!pb) return <p className="text-sm text-gray-400">Aucune cloture sur la periode</p>;
                  return (
                    <div>
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm text-gray-500">{pb.nb_ventes} ventes - {pb.nb_clotures} clotures</span>
                        <span className="text-xl font-black text-indigo-700">{fmt(pb.total_theorique)}</span>
                      </div>
                      {parseFloat(pb.ecart_total || 0) !== 0 && (
                        <p className={`text-xs mt-1 font-medium ${ecartColor(pb.ecart_total)}`}>Ecart: {fmt(pb.ecart_total)}</p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>

        {/* ===== TOTAUX CONSOLIDES ===== */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" /><span>TOTAL CONSOLIDE</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {modes.slice(0, 4).map(m => {
              const Icon = m.icon;
              const theo = parseFloat(consolide[`${m.key}_theorique`] || 0);
              return (
                <div key={m.key} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className={`bg-gradient-to-r ${m.color} px-4 py-2 flex items-center space-x-2`}>
                    <Icon className="h-4 w-4 text-white" />
                    <span className="text-white text-xs font-bold">{m.label}</span>
                  </div>
                  <div className="px-4 py-3">
                    <p className={`text-2xl font-black ${m.text}`}>{fmt(theo)}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Grand total */}
          <div className="mt-4 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl p-5 flex items-center justify-between shadow-lg">
            <div className="text-white">
              <p className="font-medium">TOTAL GENERAL</p>
              <p className="text-xs opacity-70">{consolide.nb_clotures || 0} clotures - {consolide.nb_ventes_total || 0} ventes</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black text-white">{fmt(consolide.total_theorique)}</p>
          </div>
        </div>

        {/* ===== COMPTES CLIENTS ===== */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => { setShowComptes(!showComptes); if (!showComptes) loadComptes(); }}>
            <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
              <Users className="h-5 w-5 text-indigo-600" /><span>COMPTES CLIENTS</span>
              {comptesClients.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                  {comptesClients.filter(c => parseFloat(c.solde_compte) < 0).length} debiteur(s)
                </span>
              )}
            </h2>
            {showComptes ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </div>
          {showComptes && (
            <div>
              <div className="px-5 py-3 border-b bg-gray-50 flex flex-col sm:flex-row gap-3">
                <input type="text" placeholder="Rechercher..." value={filtreCompte}
                  onChange={e => setFiltreCompte(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') loadComptes(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg" />
                <select value={filtreType} onChange={e => setFiltreType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white">
                  <option value="tous">Tous</option>
                  <option value="debiteurs">Debiteurs</option>
                  <option value="crediteurs">Crediteurs</option>
                  <option value="or">Solde or</option>
                </select>
                <button onClick={loadComptes} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium">Filtrer</button>
              </div>
              <div className="divide-y">
                {comptesClients.length === 0 ? <p className="px-4 py-8 text-center text-gray-500">Aucun client</p>
                : comptesClients.map(c => {
                  const solde = parseFloat(c.solde_compte||0);
                  const or_g = parseFloat(c.solde_or_grammes||0);
                  return (
                    <div key={c.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${solde < 0 ? 'bg-red-500' : solde > 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div>
                          <p className="font-bold text-gray-900">{c.nom}</p>
                          <p className="text-xs text-gray-500">{c.telephone || '-'} {c.dernier_bijou_nom ? `- Dernier: ${c.dernier_bijou_nom}` : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-black ${solde < 0 ? 'text-red-600' : solde > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {solde !== 0 ? `${solde < 0 ? '-' : ''}${fmt(Math.abs(solde))}` : '-'}
                        </p>
                        {solde < 0 && <span className="text-xs font-bold text-red-500">DOIT</span>}
                        {or_g > 0 && <span className="text-xs font-bold text-yellow-600 ml-2">{or_g}g or</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ===== HISTORIQUE CLOTURES ===== */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
              <Clock className="h-5 w-5 text-indigo-600" /><span>HISTORIQUE CLOTURES</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Boutique</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Ventes</th>
                  <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Theorique</th>
                  <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Compte</th>
                  <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Ecart</th>
                  <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {historique.length === 0 ? <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Aucune cloture</td></tr>
                : historique.slice(0, 50).map(c => {
                  const ecart = parseFloat(c.ecart_total||0);
                  return (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{c.boutique_nom}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(c.date_cloture)}</td>
                      <td className="px-4 py-3 text-center">{c.nb_ventes || 0}</td>
                      <td className="px-4 py-3 text-right font-medium">{fmt(c.total_theorique)}</td>
                      <td className="px-4 py-3 text-right">{fmt(c.total_compte)}</td>
                      <td className="px-4 py-3 text-right"><span className={`px-2 py-0.5 rounded font-bold text-xs ${ecartColor(ecart)}`}>{fmt(ecart)}</span></td>
                      <td className="px-4 py-3 text-center"><button onClick={() => setShowDetailModal(c)} className="text-indigo-600 hover:text-indigo-800"><Eye className="h-4 w-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal detail cloture */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl sm:my-8 max-h-[95vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div><h3 className="font-semibold">Detail cloture</h3><p className="text-sm text-indigo-200">{showDetailModal.boutique_nom}</p></div>
              <button onClick={() => setShowDetailModal(null)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Ouverture</p><p className="font-medium">{fmtDate(showDetailModal.date_ouverture)}</p></div>
                <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500">Cloture</p><p className="font-medium">{fmtDate(showDetailModal.date_cloture)}</p></div>
              </div>
              <table className="w-full text-sm border rounded-xl overflow-hidden">
                <thead className="bg-gray-50">
                  <tr><th className="px-4 py-2 text-left text-xs text-gray-500">Mode</th><th className="px-4 py-2 text-right text-xs text-gray-500">Theorique</th><th className="px-4 py-2 text-right text-xs text-gray-500">Compte</th><th className="px-4 py-2 text-right text-xs text-gray-500">Ecart</th></tr>
                </thead>
                <tbody className="divide-y">
                  {[{l:'Especes',t:showDetailModal.total_especes_theorique,c:showDetailModal.total_especes_compte},{l:'Carte',t:showDetailModal.total_carte_theorique,c:showDetailModal.total_carte_compte},{l:'Cheque',t:showDetailModal.total_cheque_theorique,c:showDetailModal.total_cheque_compte},{l:'Virement',t:showDetailModal.total_virement_theorique,c:showDetailModal.total_virement_compte}].map(r => {
                    const d = parseFloat(r.t||0) - parseFloat(r.c||0);
                    return <tr key={r.l}><td className="px-4 py-2 font-medium">{r.l}</td><td className="px-4 py-2 text-right">{fmt(r.t)}</td><td className="px-4 py-2 text-right">{fmt(r.c)}</td><td className="px-4 py-2 text-right"><span className={`px-2 py-0.5 rounded font-bold ${ecartColor(d)}`}>{fmt(d)}</span></td></tr>;
                  })}
                  <tr className="bg-indigo-50 font-bold">
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right">{fmt(showDetailModal.total_theorique)}</td>
                    <td className="px-4 py-3 text-right">{fmt(showDetailModal.total_compte)}</td>
                    <td className="px-4 py-3 text-right"><span className={`px-2 py-0.5 rounded font-bold ${ecartColor(showDetailModal.ecart_total)}`}>{fmt(showDetailModal.ecart_total)}</span></td>
                  </tr>
                </tbody>
              </table>
              {showDetailModal.commentaire && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"><p className="text-xs text-yellow-600 font-medium mb-1">Commentaire</p><p className="text-gray-700">{showDetailModal.commentaire}</p></div>}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setShowDetailModal(null)} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaisseGenerale;
